import api from "../api";
import { Chapter, CreateChapterDto } from "../interfaces/chapters.interface";
import { Question } from "../interfaces/questions.interface";

const ensureQuestionStructure = (question: any): Question | null => {
  if (!question) return null;

  return {
    _id: question._id || "",
    questionAnnouncement: question.questionAnnouncement || "",
    category: question.category || "",
    answerA: question.answerA || "",
    answerB: question.answerB || "",
    answerC: question.answerC || "",
    answerD: question.answerD || "",
    chapter: question.chapter || "",
    fileName: question.fileName,
    correctAnswers: Array.isArray(question.correctAnswers)
      ? question.correctAnswers
      : [],
  };
};

export class ChapterService {
  public async getChapters(): Promise<Chapter[]> {
    const response = await api.get("/chapters");
    return response.data.data;
  }

  public async getChapterById(chapterId: string): Promise<Chapter> {
    // Get chapter with populated questions
    const response = await api.get(`/chapters/${chapterId}`);
    const chapter = response.data.data;

    // Filter and validate questions
    const validQuestions = (chapter.questions || [])
      .map(ensureQuestionStructure)
      .filter((q: Question | null): q is Question => q !== null);

    return {
      ...chapter,
      questions: validQuestions,
    };
  }

  public async createChapter(chapterData: CreateChapterDto): Promise<Chapter> {
    // First create the chapter
    const { questions, ...chapterInfo } = chapterData;
    const chapterResponse = await api.post(
      "/chapters/transporter",
      chapterInfo
    );
    const createdChapter = chapterResponse.data.data;

    // If there are questions, create them
    if (questions && questions.length > 0) {
      for (const question of questions) {
        await api.post("/questions", {
          ...question,
          chapter: createdChapter._id,
        });
      }
    }

    return this.getChapterById(createdChapter._id);
  }

  public async updateChapter(
    chapterId: string,
    chapterData: CreateChapterDto
  ): Promise<Chapter> {
    // First update the chapter basic info
    const { questions, ...chapterInfo } = chapterData;
    await api.put(`/chapters/${chapterId}`, chapterInfo);

    // Get existing questions
    const currentChapter = await this.getChapterById(chapterId);
    const existingQuestions = currentChapter.questions || [];

    // Update or create questions
    if (questions && questions.length > 0) {
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        if (existingQuestions[i] && existingQuestions[i]._id) {
          // Update existing question
          await api.put(`/questions/${existingQuestions[i]._id}`, {
            ...question,
            chapter: chapterId,
          });
        } else {
          // Create new question
          await api.post("/questions", {
            ...question,
            chapter: chapterId,
          });
        }
      }
    }

    // Delete any remaining old questions
    if (existingQuestions.length > (questions?.length || 0)) {
      for (let i = questions?.length || 0; i < existingQuestions.length; i++) {
        if (existingQuestions[i]._id) {
          await api.delete(`/questions/${existingQuestions[i]._id}`);
        }
      }
    }

    // Return the updated chapter with questions
    return this.getChapterById(chapterId);
  }

  public async deleteChapter(chapterId: string): Promise<void> {
    await api.delete(`/chapters/${chapterId}`);
  }
}

export default ChapterService;
